import { useState } from 'react';
import { Alert, Button, Input, Modal, Select, Tag } from 'antd';
import {
  useDeviceHeartbeats,
  useDeviceModeHistory,
  modeSaveError,
  type DeviceScope,
  type SaveDeviceMode,
  type UnifiedDevice,
} from './useDeviceHeartbeats';
import {
  heartbeatPresentation,
  heartbeatTime,
  modeLabels,
  type OperatingMode,
} from './presentation';
import './heartbeat.css';

export function HeartbeatBadge({ device }: { device: UnifiedDevice }) {
  const display = heartbeatPresentation(device.heartbeat);
  return <Tag color={display.color}>{display.label}</Tag>;
}

export function DeviceModeEditor({
  device,
  onSave,
  onRefresh,
  alertOwnership = device.alertSetup?.status === 'active'
    ? 'active'
    : device.alertSetup?.status === 'legacy' || device.alertSetup?.status === 'prepared'
    ? 'legacy'
    : 'unknown',
}: {
  device: UnifiedDevice;
  onSave: (command: SaveDeviceMode) => Promise<unknown>;
  onRefresh: () => void;
  alertOwnership?: 'active' | 'legacy' | 'unknown';
}) {
  const [draft, setDraft] = useState<{ mode: OperatingMode; version: number } | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  async function save() {
    if (!draft || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        orgId: device.orgId,
        sensorId: device.sensorId,
        mode: draft.mode,
        expectedVersion: draft.version,
        note: note.trim(),
      });
      setDraft(null);
      setSaved(true);
    } catch (failure) {
      setError(modeSaveError(failure));
    } finally {
      setSaving(false);
    }
  }
  return (
    <>
      <Button
        size="small"
        onClick={() => {
          setDraft({ mode: device.mode.mode, version: device.mode.version });
          setNote('');
          setError(null);
          setSaved(false);
        }}
      >
        Skift driftsform
      </Button>
      {saved && <span role="status">Driftsformen er gemt.</span>}
      <Modal
        open={Boolean(draft)}
        title={`Driftsform · ${device.sensorTitle}`}
        okText="Gem driftsform"
        cancelText="Annuller"
        confirmLoading={saving}
        closable={!saving}
        maskClosable={!saving}
        cancelButtonProps={{ disabled: saving }}
        okButtonProps={{
          disabled:
            !draft || draft.mode === device.mode.mode || draft.version !== device.mode.version,
        }}
        onCancel={() => !saving && setDraft(null)}
        onOk={save}
      >
        <p>Vælg hvordan enheden indgår i den fælles alarmovervågning.</p>
        <Select
          aria-label="Ny driftsform"
          value={draft?.mode}
          onChange={(mode) => setDraft((current) => current && { ...current, mode })}
          disabled={saving}
          style={{ width: '100%' }}
          options={Object.entries(modeLabels).map(([value, label]) => ({ value, label }))}
        />
        <ul className="heartbeat-mode-help">
          <li>Live: med i det daglige driftsoverblik.</li>
          <li>Test: testhændelser holdes adskilt fra livedrift.</li>
          <li>Ikke i brug: den fælles datakontrol sættes på pause.</li>
        </ul>
        {alertOwnership !== 'active' && (
          <Alert
            type="warning"
            message={
              alertOwnership === 'legacy'
                ? 'Den eksisterende alarmstyring er stadig i brug. Test og Ikke i brug stopper først dens beskeder, når opsætningen er overført og aktiveret.'
                : 'Det er ikke bekræftet, om enheden bruger den samlede alarmstyring. Test og Ikke i brug kan derfor ikke bekræftes at stoppe eksisterende beskeder.'
            }
          />
        )}
        <label htmlFor="heartbeat-mode-note">Bemærkning til historikken</label>
        <Input.TextArea
          id="heartbeat-mode-note"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={2000}
          rows={3}
          disabled={saving}
        />
        {error && (
          <Alert
            className="heartbeat-error"
            role="alert"
            type="error"
            showIcon
            message={error}
            action={
              <Button size="small" onClick={onRefresh}>
                Opdatér enheden
              </Button>
            }
          />
        )}
        {draft && draft.version !== device.mode.version && (
          <Alert
            className="heartbeat-error"
            type="warning"
            message={`Aktuel driftsform er ${
              modeLabels[device.mode.mode]
            }. Luk dialogen og kontrollér dit valg igen.`}
          />
        )}
      </Modal>
    </>
  );
}

export function ModeHistory({ scope }: { scope: DeviceScope }) {
  const [open, setOpen] = useState(false);
  const query = useDeviceModeHistory(scope, open);
  const history = query.data?.pages.flatMap((page) => page.history) || [];
  return (
    <div className="heartbeat-history">
      <Button type="link" size="small" aria-expanded={open} onClick={() => setOpen(!open)}>
        {open ? 'Skjul driftsformens historik' : 'Vis driftsformens historik'}
      </Button>
      {open && (
        <div>
          {query.isPending && <p role="status">Henter historik…</p>}
          {query.isError && (
            <Alert
              type="warning"
              message="Historikken kunne ikke hentes"
              action={<Button onClick={() => query.refetch()}>Prøv igen</Button>}
            />
          )}
          {!query.isPending && !query.isError && !history.length && (
            <p>Ingen ændringer registreret.</p>
          )}
          <ol>
            {history.map((entry) => (
              <li key={entry.version}>
                <strong>
                  {modeLabels[entry.previousMode]} → {modeLabels[entry.mode]}
                </strong>
                <small>
                  {heartbeatTime(entry.updatedAt)} · {entry.updatedBy}
                </small>
                {entry.note && <p>{entry.note}</p>}
              </li>
            ))}
          </ol>
          {query.hasNextPage && (
            <Button loading={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
              Hent ældre ændringer
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/** Mount inside the existing AdminOnly content boundary; no customer data requests. */
export default function DeviceHeartbeatPanel({ orgId, sensorId }: DeviceScope) {
  const scope = { orgId, sensorId };
  const query = useDeviceHeartbeats(scope);
  const device = query.data?.find((item) => item.orgId === orgId && item.sensorId === sensorId);
  const display = device && heartbeatPresentation(device.heartbeat);
  return (
    <section className="device-heartbeat-panel" aria-label="Status og seneste data">
      <div className="heartbeat-heading">
        <h3>Status og seneste data</h3>
        <Button size="small" type="link" loading={query.isFetching} onClick={() => query.refetch()}>
          Opdatér
        </Button>
      </div>
      {query.isPending && <p role="status">Henter datakontrol…</p>}
      {query.isError && (
        <Alert
          type="warning"
          message="Datakontrollen kunne ikke opdateres"
          description="Driftsform og datastatus kan ikke bekræftes. Prøv igen."
        />
      )}
      {!query.isPending && !query.isError && !device && (
        <p>Enheden har endnu ingen registreret datakontrol eller driftsform.</p>
      )}
      {device && (
        <>
          <div className="heartbeat-heading">
            <Tag color={device.mode.mode === 'test' ? 'blue' : undefined}>
              {modeLabels[device.mode.mode]}
            </Tag>
          </div>
          {device.mode.version === 0 && (
            <p className="heartbeat-muted">
              Driftsformen følger enhedens registrering, indtil den ændres her.
            </p>
          )}
          {!query.isError && <HeartbeatBadge device={device} />}
          {!query.isError && (
            <Tag>
              {device.openIncidentCount} åbne{' '}
              {device.alertSetup?.status === 'active' ? 'hændelser' : 'fælles hændelser'} i{' '}
              {modeLabels[device.mode.mode]}
            </Tag>
          )}
          {!query.isError && device.alertSetup?.status !== 'active' && (
            <p className="heartbeat-muted">Eksisterende alarmer er ikke med i dette tal.</p>
          )}
          <dl className="heartbeat-facts">
            <div>
              <dt>Seneste faktiske data</dt>
              <dd>{heartbeatTime(device.heartbeat.lastDataAt)}</dd>
            </div>
            <div>
              <dt>Senest forventet data</dt>
              <dd>
                {device.heartbeat.expectedAfter === null
                  ? 'Ingen fast frist'
                  : heartbeatTime(device.heartbeat.expectedAfter)}
                {device.heartbeat.ruleMode === 'test' && <Tag color="blue">Test-regel</Tag>}
              </dd>
            </div>
            <div>
              <dt>Tilladt datalder</dt>
              <dd>
                {device.heartbeat.maxAgeMinutes === null
                  ? 'Ikke fastsat'
                  : `${device.heartbeat.maxAgeMinutes} minutter`}
              </dd>
            </div>
            <div>
              <dt>Kontrollen blev udført</dt>
              <dd>{heartbeatTime(device.heartbeat.evaluatedAt)}</dd>
            </div>
          </dl>
          <p className="heartbeat-muted">
            Fristen beregnes fra seneste data og den gældende regel. Kontroltidspunktet er ikke en
            ny måling.
          </p>
          {display?.evaluationStale && (
            <Alert
              type="warning"
              showIcon
              message="Datakontrollen er ikke opdateret"
              description="Den seneste vurdering bekræfter ikke enhedens tilstand lige nu."
            />
          )}
          {device.heartbeat.status === 'unsupported' && (
            <p>
              Enheden har ingen understøttet, fast datakontrol. En SMS-alarm er en hændelse og
              beviser ikke løbende datalevering.
            </p>
          )}
          {device.heartbeat.status === 'paused' && (
            <p>Datakontrollen er pauset. Se driftsformen og den gældende regel.</p>
          )}
        </>
      )}
    </section>
  );
}
